import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';

import IconButton from 'components/@extended/IconButton';
import NotificationDetailModal from 'components/NotificationDetailModal';
import { Notification } from 'iconsax-reactjs';
import {
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type InboxItem
} from 'services/eventNotifications.service';
import { formatRelativeTime, formatTimeOnly } from 'utils/notificationDisplay';

const MENU_LIMIT = 8;

/** Bell list + badge use the same unread-only filter as the API. */
const BELL_INBOX_QUERY = { unreadOnly: true as const };

function notificationTitle(row: InboxItem): string {
  return (row.subject || row.bodyPreview || 'Notification').trim() || 'Notification';
}

export default function NotificationBell() {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const open = Boolean(anchor);

  /** Lightweight fetch so the bell badge shows unread count before the menu is opened. */
  const refreshBadgeCount = useCallback(async () => {
    try {
      const data = await fetchMyNotifications(1, 1, BELL_INBOX_QUERY);
      setUnreadCount(data?.unreadCount ?? 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyNotifications(1, MENU_LIMIT, BELL_INBOX_QUERY);
      setItems(data?.items ?? []);
      setUnreadCount(data?.unreadCount ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshBadgeCount();
  }, [refreshBadgeCount]);

  const handleOpen = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    await load();
  };

  const handleClose = () => setAnchor(null);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      await load();
    } catch {
      /* ignore */
    }
  };

  const handleItemClick = async (row: InboxItem) => {
    try {
      if (row.isRead !== 'read') {
        await markNotificationRead(row._id);
      }
      await load();
    } catch {
      /* ignore */
    }
    handleClose();
    setDetailId(row._id);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setDetailId(null);
    void refreshBadgeCount();
  };

  return (
    <>
      <IconButton
        size="large"
        color="secondary"
        variant="light"
        onClick={handleOpen}
        aria-label="notifications"
        sx={{ color: 'secondary.main' }}
      >
        <Badge color="error" badgeContent={unreadCount > 99 ? '99+' : unreadCount} invisible={unreadCount === 0}>
          <Notification variant="Bold" size={22} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'calc(100vw - 24px)', sm: 480 },
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: 2,
              mt: 1,
              boxShadow: (theme) => theme.shadows[8],
              overflow: 'hidden',
              py: 0
            }
          },
          list: {
            sx: {
              py: 0,
              width: '100%',
              maxWidth: '100%'
            }
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          <Button
            variant="text"
            size="small"
            disabled={unreadCount === 0 || loading}
            onClick={() => void handleMarkAllRead()}
            sx={{
              color: 'warning.main',
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 0,
              px: 0.5,
              '&.Mui-disabled': { color: 'warning.main', opacity: 0.45 }
            }}
          >
            Mark all read
          </Button>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No unread notifications.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: 'auto', py: 1.5, px: 2 }}>
            {items.map((row) => (
              <Box
                key={row._id}
                onClick={() => void handleItemClick(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void handleItemClick(row);
                  }
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: row.isRead === 'read' ? 'background.paper' : 'primary.lighter',
                  p: 1.5,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:last-of-type': { mb: 0 },
                  '&:hover': {
                    bgcolor: row.isRead === 'read' ? 'action.hover' : 'primary.light'
                  }
                }}
              >
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.45, pr: 0.5 }}>
                  {notificationTitle(row)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.created_at ? formatRelativeTime(row.created_at) : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.created_at ? formatTimeOnly(row.created_at) : ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Divider />
        <Box sx={{ py: 0 }}>
          <Typography
            component={RouterLink}
            to="/notifications"
            onClick={handleClose}
            variant="body2"
            sx={{
              color: 'primary.main',
              textAlign: 'center',
              display: 'block',
              py: 1.5,
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            View all
          </Typography>
        </Box>
      </Menu>
      <NotificationDetailModal notificationId={detailId} open={detailOpen} onClose={handleDetailClose} />
    </>
  );
}
